import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import IShopify from "../../../../../models/IShopify";
import ICatalog from "../../../../../models/ICatalog";
import IShopifyUpdateShopDTO from "../../../../../models/DTO/IShopifyUpdateShopDTO";
import IShopifyCreateShopDTO from "../../../../../models/DTO/IShopifyCreateShopDTO";
import UserContext from "../../../../../contexts/UserContext";
import SupplierContext from "../../../../../contexts/SupplierContext";
import shopifyLogo from "../../../../../assets/images/shopify/shopify-logo.svg";
import shopifyLightLogo from "../../../../../assets/images/shopify/shopify-logo-light.svg";
import IPrice_Group from "../../../../../models/IPrice_Group";
import OwTitle from "../../../../shared/OwTitle/OwTitle";
import EndpointsEnum from "../../../../../models/EndpointsEnum";
import IShopifyOnUpdateShopDTO from "../../../../../models/DTO/IShopifyOnUpdateShopDTO";
import INotification from "../../../../../models/INotification";
import NotificationPositionEnum from "../../../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../../../models/NotificationLevelEnum";
import OwButton from "../../../../shared/OwButton/OwButton";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../../../shared/OwFnInput/OwFnInput";
import OwSelectOption from "../../../../shared/OwSelectOption/OwSelectOption";
import { SocketContext } from "../../../../../contexts/SocketContext";
import UiContext from "../../../../../contexts/UiContext";
import UIService from "../../../../../services/UIService";

type Props = {
    closeModalFn: () => void;
    shopifyShop?: IShopify;
    catalogs: ICatalog[];
    create: (payload: IShopifyCreateShopDTO) => void;
    update: (payload: IShopifyUpdateShopDTO) => void;
    isMainViewLight?: boolean;
};

export const ShopifyShopModalPrivateApp = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket: Socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const getLogo = () => {
        let logo = shopifyLogo;

        if (UIService.isMainViewLight()) {
            logo = shopifyLightLogo;
        }

        return logo;
    };

    const [shopifyShop, setShopifyShop] = useState<Partial<IShopify>>(
        props.shopifyShop || { shopify_shop: "", skip_inventory_updates: false, skip_pricing_updates: false }
    );
    const [catalogId, setCatalogId] = useState<number | undefined>(props.shopifyShop?.catalog_id);
    const [priceGroupId, setPriceGroupId] = useState<number | undefined>(props.shopifyShop?.price_group_id);
    const [priceGroups, setPriceGroups] = useState<IPrice_Group[]>([]);
    const [logo, setLogo] = useState(getLogo());
    const [disableSave, setDisableSave] = useState(false);

    const [apiKey, setApiKey] = useState("");
    const [apiSecret, setApiSecret] = useState("");
    const [adminApiToken, setAdminApiToken] = useState("");
    const [storefrontApiToken, setStorefrontApiToken] = useState("");

    useEffect(() => {
        setupPriceGroups();
    }, [catalogId]);

    const setupPriceGroups = () => {
        if (shopifyShop && shopifyShop.catalog_id) {
            const selectedCatalog = props.catalogs.find((catalog) => catalog.id.toString() === (shopifyShop.catalog_id || "").toString());

            if (selectedCatalog) {
                setPriceGroups(selectedCatalog.Price_Groups || []);
            }
        }
    };

    useEffect(() => {
        setupPriceGroups();

        socket.on(EndpointsEnum.ShopifyOnUpdateShop, onUpdateShop);

        return () => {
            socket.removeListener(EndpointsEnum.ShopifyOnUpdateShop, onUpdateShop);
        };
    }, []);

    const closeModal = () => {
        props.closeModalFn();
    };

    const update = () => {
        if (catalogId && priceGroupId) {
            const selectedCatalog = props.catalogs.find((catalog) => catalog.id.toString() === catalogId.toString());

            if (props.shopifyShop && selectedCatalog) {
                const selectedPriceGroup = (selectedCatalog.Price_Groups || []).find(
                    (priceGroup) => priceGroup.id.toString() === shopifyShop.price_group_id?.toString()
                );

                const catalogGuid = selectedCatalog?.guid || "";
                const priceGroupGuid = selectedPriceGroup?.guid || "";

                const payload: IShopifyUpdateShopDTO = {
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                    priceGroupGuid,
                    catalogGuid,
                    shopGuid: props.shopifyShop.guid,
                };

                props.update(payload);
            }
        }
    };

    const onUpdateShop = (data: IShopifyOnUpdateShopDTO) => {
        const notification: INotification = {
            message: "successfully_updated_shop",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);

        props.closeModalFn();
    };

    const create = () => {
        const selectedCatalog = props.catalogs.find((catalog) => catalog.id.toString() === shopifyShop.catalog_id?.toString());

        if (selectedCatalog && shopifyShop.shopify_shop) {
            const selectedPriceGroup = (selectedCatalog.Price_Groups || []).find(
                (priceGroup) => priceGroup.id.toString() === shopifyShop.price_group_id?.toString()
            );

            const catalogGuid = selectedCatalog?.guid || "";
            const priceGroupGuid = selectedPriceGroup?.guid || "";

            const payload: IShopifyCreateShopDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                catalogGuid,
                priceGroupGuid,
                shopify_shop: shopifyShop.shopify_shop,
                skip_inventory_updates: shopifyShop.skip_inventory_updates || false,
                skip_pricing_updates: shopifyShop.skip_pricing_updates || false,
                active: true,
                api_key: apiKey,
                api_secret: apiSecret,
                admin_api_token: adminApiToken,
                storefront_api_token: storefrontApiToken,
                shopify_options: {},
                shopify_fields_to_include: {
                    name: true,
                    sku: true,
                    description: true,
                },
            };

            props.create(payload);
        }
    };

    const existingShopName = (name: string): boolean => {
        let exists = false;

        const shops = supplierModel.Shopifies || [];

        for (const shop of shops) {
            if (shop.shopify_shop === name) {
                exists = true;
            }
        }

        return exists;
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;

        const _shop = {
            ...shopifyShop,
        };

        // @ts-expect-error
        _shop[name] = value;

        setShopifyShop(_shop);

        if (name === "catalog_id") {
            setCatalogId(Number(value));
        }

        if (name === "price_group_id") {
            setPriceGroupId(Number(value));
        }

        if (name === "shopify_shop") {
            if (existingShopName(value)) {
                setDisableSave(true);
            } else {
                setDisableSave(false);
            }
        }

        if (name === "api_key") {
            setApiKey(value);
        }

        if (name === "api_secret") {
            setApiSecret(value);
        }

        if (name === "admin_api_token") {
            setAdminApiToken(value);
        }

        if (name === "storefront_api_token") {
            setStorefrontApiToken(value);
        }
    };

    return (
        <div className={"ShopifyShopModalPrivateApp p-2"}>
            <div className={"w-100"}>
                <div className={""}>
                    <div className={"d-flex justify-content-between"}>
                        <OwTitle title={props.shopifyShop?.guid ? "update_shopify_shop" : "add_shopify_shop"} tag={"h3"} />
                        <div>
                            <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeModal} />
                        </div>
                    </div>
                    <div>
                        <img src={logo} height={32} />
                    </div>
                </div>
            </div>

            <div>
                <div>
                    <OwLabel title={"name"} />
                    <OwFnInput fn={handleChange} name={"shopify_shop"} value={shopifyShop.shopify_shop} disabled={!!props.shopifyShop} />
                    {disableSave ? (
                        <div>
                            <OwLabel title={"shop_with_existing_name"} className={"text-danger"} />
                        </div>
                    ) : null}
                </div>

                <div className={"mt-3"}>
                    <OwLabel title={"catalog"} />
                    <OwSelectOption
                        name={"catalog_id"}
                        options={props.catalogs}
                        fn={handleChange}
                        selectedOption={shopifyShop}
                        selectedOptionProp={"catalog_id"}
                        valueProp={"id"}
                        titleProp={"name"}
                    />
                </div>

                <div className={"mt-3"}>
                    <OwLabel title={"price_group"} />
                    <OwSelectOption
                        name={"price_group_id"}
                        options={priceGroups}
                        fn={handleChange}
                        selectedOption={shopifyShop}
                        selectedOptionProp={"price_group_id"}
                        valueProp={"id"}
                        titleProp={"value"}
                    />
                </div>

                <div className={"mt-3"}>
                    <OwLabel title={"api_key"} />
                    <OwFnInput fn={handleChange} name={"api_key"} value={shopifyShop.api_key} />
                </div>

                <div className={"mt-3"}>
                    <OwLabel title={"api_secret"} />
                    <OwFnInput fn={handleChange} name={"api_secret"} value={shopifyShop.api_secret} />
                </div>

                <div className={"mt-3"}>
                    <OwLabel title={"admin_api_token"} />
                    <OwFnInput fn={handleChange} name={"admin_api_token"} value={shopifyShop.admin_api_token} />
                </div>

                <div className={"mt-3"}>
                    <OwLabel title={"storefront_api_token"} />
                    <OwFnInput fn={handleChange} name={"storefront_api_token"} value={shopifyShop.storefront_api_token} />
                </div>
            </div>
            <div className={"mt-3"}>
                <OwButton
                    title={props.shopifyShop?.guid ? "update" : "create"}
                    fn={props.shopifyShop?.guid ? update : create}
                    disabled={disableSave}
                />
                <OwButton className={"btn-danger ms-3"} title={"cancel"} fn={closeModal} />
            </div>
        </div>
    );
};
